import React, { createContext, useCallback, useContext, useState } from 'react'
import apiService from '../api/apiService'

const UndoContext = createContext()

export function useUndo() {
  return useContext(UndoContext)
}

export function UndoProvider({ children }) {
  const [undoStack, setUndoStack] = useState([])
  const [isUndoing, setIsUndoing] = useState(false)
  const [refreshCallback, setRefreshCallback] = useState(null)

  const addToUndoStack = useCallback((operation) => {
    setUndoStack((prev) => [...prev, operation])
  }, [])

  const setRefresh = useCallback((callback) => {
    setRefreshCallback(() => callback)
  }, [])

  const undo = useCallback(async () => {
    if (undoStack.length === 0 || isUndoing) return

    setIsUndoing(true)
    const lastOperation = undoStack[undoStack.length - 1]

    try {
      // Use the central undo API endpoint
      await apiService.performUndo(lastOperation)

      // Remove the operation from the stack
      setUndoStack((prev) => prev.slice(0, -1))

      // Ensure all state updates are completed before refreshing
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Call refresh callback if it exists
      if (refreshCallback) {
        try {
          await refreshCallback()
        } catch (refreshError) {
          throw refreshError
        }
      } else {
        console.warn('No refresh callback available')
      }
    } catch (error) {
      console.error('Error undoing operation:', error)
      throw error
    } finally {
      setIsUndoing(false)
    }
  }, [undoStack, isUndoing, refreshCallback])

  const clearUndoStack = useCallback(() => {
    setUndoStack([])
  }, [])

  const value = {
    undoStack,
    isUndoing,
    addToUndoStack,
    undo,
    clearUndoStack,
    setRefresh,
    canUndo: undoStack.length > 0 && !isUndoing,
  }

  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>
}
